import { ScrollArea } from "@/components/ui/scroll-area";
import { useContextBarAppearance } from "@/contexts/ContextBarAppearanceContext";
import TodoItem from "@/domain/entities/TodoItem";
import useManageTodo from "@/hooks/useManageTodo";
import { cn } from "@/lib/utils";
import { t } from "i18next";
import { useEffect, useState } from "react";
import TodoAcceptedLine from "./TodoAcceptedLine";
import TodoProposedLine from "./TodoProposedLine";

export enum TodoListVariant {
    THREAD = "thread",
    CUSTOM_VIEW = "customView",
}

interface TodoListProps {
    todos: TodoItem[];
    variant?: TodoListVariant;
    areKeptTodos?: boolean;
}

const TodoList = ({
    todos,
    variant = TodoListVariant.THREAD,
    areKeptTodos = false,
}: TodoListProps) => {
    const { setTodoState, deleteTodo, addTodoToDashboard } = useManageTodo();
    const { isDeepFocus } = useContextBarAppearance();

    const filterTasks = (
        toFilterTasks: TodoItem[],
        isUserAccepted: Boolean = true,
    ) =>
        toFilterTasks?.filter((task) => task.isUserAccepted === isUserAccepted);

    const [acceptedTodos, setAcceptedTodos] = useState<TodoItem[]>(
        filterTasks(todos),
    );
    const [proposedTodos, setProposedTodos] = useState<TodoItem[]>(
        filterTasks(todos, false),
    );

    useEffect(() => {
        setAcceptedTodos(filterTasks(todos));
        setProposedTodos(filterTasks(todos, false));
    }, [todos]);

    const handleRemoveTodo = async (todoToRemove: TodoItem) => {
        const prevAcceptedTodos = [...acceptedTodos];
        const prevProposedTodos = [...proposedTodos];
        // Un todo conservé retiré disparaît : le proposer à nouveau (« + ») le ferait revenir
        const newProposedTodos = areKeptTodos
            ? prevProposedTodos
            : [...prevProposedTodos, todoToRemove];
        const newAcceptedTodos = prevAcceptedTodos.filter(
            (todo) => todo.id !== todoToRemove.id,
        );
        setAcceptedTodos(newAcceptedTodos);
        setProposedTodos(newProposedTodos);
        const success = await deleteTodo(todoToRemove);
        if (!success) {
            setAcceptedTodos(prevAcceptedTodos);
            setProposedTodos(prevProposedTodos);
        }
    };

    const handleAcceptTodo = async (todoToAccept: TodoItem) => {
        const prevAcceptedTodos = [...acceptedTodos];
        const prevProposedTodos = [...proposedTodos];
        const newAcceptedTodos = [...prevAcceptedTodos, todoToAccept];
        const newProposedTodos = proposedTodos.filter(
            (todo) => todo.id !== todoToAccept.id,
        );

        setAcceptedTodos(newAcceptedTodos);
        setProposedTodos(newProposedTodos);
        const success = await addTodoToDashboard(todoToAccept);
        if (!success) {
            setAcceptedTodos(prevAcceptedTodos);
            setProposedTodos(prevProposedTodos);
        }
    };

    const handleCompletionToggle = async (todoToUpdate: TodoItem) => {
        const prevAcceptedTodos = [...acceptedTodos];
        const newAcceptedTodos = prevAcceptedTodos.map((todo) =>
            todo.id === todoToUpdate.id &&
            todo.internetMessageId === todoToUpdate.internetMessageId
                ? new TodoItem({ ...todo, done: !todoToUpdate.done })
                : todo,
        );

        setAcceptedTodos(newAcceptedTodos);
        const success = await setTodoState(todoToUpdate, !todoToUpdate.done);
        if (!success) {
            setAcceptedTodos(prevAcceptedTodos);
        }
    };

    if (!todos || todos.length === 0) return null;

    // Dernier todo conservé retiré : « Aucune tâche » tout de suite, sans attendre le rafraîchissement du widget
    if (
        areKeptTodos &&
        acceptedTodos.length === 0 &&
        proposedTodos.length === 0
    ) {
        return (
            <p
                className={cn(
                    "px-4 text-left text-sm",
                    isDeepFocus && "text-white",
                )}
            >
                {t("widgets.todo.noTodo")}
            </p>
        );
    }

    const isCustomView = variant === TodoListVariant.CUSTOM_VIEW;

    return (
        <div
            className={cn(
                "flex flex-col font-body text-sm",
                isCustomView && "min-h-0 flex-1",
                isDeepFocus ? "text-white" : "text-primary-dark",
            )}
        >
            <ScrollArea
                className={cn(isCustomView && "min-h-0 flex-1")}
                viewportClassName={cn(isCustomView ? "h-full" : "max-h-55")}
            >
                {acceptedTodos.map((todo) => (
                    <TodoAcceptedLine
                        key={`${todo.id}-${todo.internetMessageId}`}
                        handleCompletionToggle={handleCompletionToggle}
                        handleRemoveTodo={handleRemoveTodo}
                        todo={todo}
                    />
                ))}
                {proposedTodos.map((todo) => (
                    <TodoProposedLine
                        key={`${todo.id}-${todo.internetMessageId}`}
                        handleAcceptTodo={handleAcceptTodo}
                        todo={todo}
                    />
                ))}
            </ScrollArea>
        </div>
    );
};

export default TodoList;
